import React, { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowUpRight, Loader2 } from "lucide-react";
import InstagramFeed from "@/components/InstagramFeed";
import TurnaroundStrip from "@/components/TurnaroundStrip";

const APPAREL_SUBCATS = ["tees", "tanks", "hoodies", "zip-ups"];
const SC_SUBCATS = ["stickers", "transfer-decals", "card-stickers", "label-sheets", "door-signs", "signage"];

const categoryLabels = {
  tees: "T-Shirts",
  tanks: "Tank Tops",
  hoodies: "Hoodies",
  "zip-ups": "Zip-Ups",
  gangsheets: "Gang Sheet",
  stickers: "Peel & Stick",
  "transfer-decals": "Transfer Decal",
  "card-stickers": "Card Sticker",
  "label-sheets": "Label Sheets",
  "door-signs": "Door Sign",
  signage: "Signage",
  marketing: "Marketing",
};

const categories = [
  { id: "all", label: "All", activeStyle: { background: "#111", color: "#fff", borderColor: "#111" }, match: () => true },
  { id: "apparel", label: "Apparel", activeStyle: { background: "var(--dtf-blue)", color: "#fff", borderColor: "var(--dtf-blue)" }, match: (p) => APPAREL_SUBCATS.includes(p.category) },
  { id: "tees", label: "T-Shirts", activeStyle: { background: "var(--dtf-blue)", color: "#fff", borderColor: "var(--dtf-blue)" }, match: (p) => p.category === "tees" },
  { id: "tanks", label: "Tank Tops", activeStyle: { background: "var(--dtf-blue)", color: "#fff", borderColor: "var(--dtf-blue)" }, match: (p) => p.category === "tanks" },
  { id: "hoodies", label: "Hoodies", activeStyle: { background: "var(--dtf-blue)", color: "#fff", borderColor: "var(--dtf-blue)" }, match: (p) => p.category === "hoodies" },
  { id: "zip-ups", label: "Zip-Ups", activeStyle: { background: "var(--dtf-blue)", color: "#fff", borderColor: "var(--dtf-blue)" }, match: (p) => p.category === "zip-ups" },
  { id: "gangsheets", label: "DTF Gang Sheets", activeStyle: { background: "var(--dtf-yellow)", color: "var(--dtf-blue)", borderColor: "var(--dtf-blue)" }, match: (p) => p.category === "gangsheets" },
  { id: "sticky-chick", label: "Sticky Chick", activeStyle: { background: "var(--sc-pink)", color: "#fff", borderColor: "var(--sc-pink)" }, match: (p) => SC_SUBCATS.includes(p.category) },
  { id: "stickers", label: "Stickers", activeStyle: { background: "var(--sc-pink)", color: "#fff", borderColor: "var(--sc-pink)" }, match: (p) => p.category === "stickers" },
  { id: "transfer-decals", label: "Decals", activeStyle: { background: "var(--sc-pink)", color: "#fff", borderColor: "var(--sc-pink)" }, match: (p) => p.category === "transfer-decals" },
  { id: "card-stickers", label: "Card Stickers", activeStyle: { background: "var(--sc-pink)", color: "#fff", borderColor: "var(--sc-pink)" }, match: (p) => p.category === "card-stickers" },
  { id: "label-sheets", label: "Label Sheets", activeStyle: { background: "var(--sc-purple)", color: "#fff", borderColor: "var(--sc-purple)" }, match: (p) => p.category === "label-sheets" },
  { id: "door-signs", label: "Door Signs", activeStyle: { background: "var(--sc-purple)", color: "#fff", borderColor: "var(--sc-purple)" }, match: (p) => p.category === "door-signs" },
  { id: "signage", label: "Signage", activeStyle: { background: "var(--sc-turquoise)", color: "#fff", borderColor: "var(--sc-turquoise)" }, match: (p) => p.category === "signage" },
  { id: "marketing", label: "Marketing", activeStyle: { background: "#111", color: "#fff", borderColor: "#111" }, match: (p) => p.category === "marketing" },
];

const DTF_CATEGORY_IDS = ["apparel", "tees", "tanks", "hoodies", "zip-ups", "gangsheets"];
const SC_CATEGORY_IDS = ["sticky-chick", "stickers", "transfer-decals", "card-stickers", "label-sheets", "door-signs", "signage"];

function detectActiveBrand(active) {
  if (DTF_CATEGORY_IDS.includes(active)) return "dtf";
  if (SC_CATEGORY_IDS.includes(active)) return "sc";
  return null;
}

function orderCategories(activeBrand) {
  const byId = Object.fromEntries(categories.map((c) => [c.id, c]));
  const dtfChips = DTF_CATEGORY_IDS.map((id) => byId[id]).filter(Boolean);
  const scChips = SC_CATEGORY_IDS.map((id) => byId[id]).filter(Boolean);
  if (activeBrand === "sc") {
    return [byId.all, ...scChips, ...dtfChips, byId.marketing].filter(Boolean);
  }
  // default: dtf-first ordering
  return [byId.all, ...dtfChips, ...scChips, byId.marketing].filter(Boolean);
}

export default function Shop({ brand = null }) {
  const [params, setParams] = useSearchParams();
  const active = params.get("category") || "all";
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api
      .get("/products")
      .then((r) => mounted && setProducts(r.data))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  // Brand override from route (/shop/dtf, /shop/tsc) takes precedence over URL-inferred brand
  const activeBrand = brand || detectActiveBrand(active);

  const orderedCategories = useMemo(() => {
    if (brand === "dtf") {
      const byId = Object.fromEntries(categories.map((c) => [c.id, c]));
      return [byId.all, ...DTF_CATEGORY_IDS.map((id) => byId[id])].filter(Boolean);
    }
    if (brand === "tsc") {
      const byId = Object.fromEntries(categories.map((c) => [c.id, c]));
      return [byId.all, ...SC_CATEGORY_IDS.map((id) => byId[id]), byId.marketing].filter(Boolean);
    }
    return orderCategories(activeBrand);
  }, [brand, activeBrand]);

  const filtered = useMemo(() => {
    const cat = categories.find((c) => c.id === active) || categories[0];
    let list = products.filter((p) => cat.match(p));

    // Hard-restrict list to selected brand when on a brand-specific shop
    if (brand === "dtf") {
      list = list.filter((p) => p.brand === "Down To Film Apparel");
    } else if (brand === "tsc") {
      list = list.filter((p) => p.brand === "The Sticky Chick");
    }

    // On the combined shop, surface products of the last-visited brand first when "All"
    if (!brand && active === "all" && activeBrand) {
      list = [...list].sort((a, b) => {
        const aIsDtf = APPAREL_SUBCATS.includes(a.category) || a.category === "gangsheets";
        const aIsSc = SC_SUBCATS.includes(a.category);
        const bIsDtf = APPAREL_SUBCATS.includes(b.category) || b.category === "gangsheets";
        const bIsSc = SC_SUBCATS.includes(b.category);
        const wanted = activeBrand === "dtf" ? [aIsDtf, bIsDtf] : [aIsSc, bIsSc];
        return (wanted[1] ? 1 : 0) - (wanted[0] ? 1 : 0);
      });
    }
    return list;
  }, [products, active, activeBrand, brand]);

  const setCat = (c) => {
    if (c === "all") setParams({});
    else setParams({ category: c });
  };

  const title = brand === "dtf" ? "SHOP DTF." : brand === "tsc" ? "SHOP TSC." : "THE SHOP.";
  const kicker = brand === "dtf"
    ? "CATALOG / DOWN TO FILM APPAREL"
    : brand === "tsc"
    ? "CATALOG / THE STICKY CHICK"
    : "CATALOG / READY-MADE";
  const titleColor = brand === "dtf" ? "text-dtf-blue" : brand === "tsc" ? "text-sc-purple" : "";

  return (
    <div data-testid="shop-page">
      <section className="border-b-2 border-black">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24">
          <p className="label-mono text-[color:var(--muted-text)]">{kicker}</p>
          <h1 className={`font-display text-6xl md:text-8xl leading-[0.9] mt-3 ${titleColor}`} data-testid="shop-title">
            {title}
          </h1>
          <p className="font-body text-[color:var(--muted-text)] max-w-xl mt-4">
            Off-the-rack pricing on our most-ordered items. Need something
            custom? Hit the quote button — we&rsquo;ll get you a real number, fast.
          </p>
        </div>
      </section>

      <TurnaroundStrip />

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="flex flex-wrap gap-3 mb-10" data-testid="category-filter">
          {orderedCategories.map((c) => {
            const isActive = active === c.id;
            return (
              <button
                key={c.id}
                onClick={() => setCat(c.id)}
                data-testid={`filter-${c.id}`}
                className="label-mono px-4 py-2 border border-black transition-colors"
                style={
                  isActive
                    ? c.activeStyle
                    : { background: "transparent", color: "#000", borderColor: "#000" }
                }
              >
                {c.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center gap-3 label-mono" data-testid="products-loading">
            <Loader2 className="animate-spin" size={16} /> Loading catalog...
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((p) => {
              const isSC = p.brand === "The Sticky Chick";
              const catLabel = categoryLabels[p.category] || p.category;
              return (
                <Link
                  key={p.id}
                  to={`/shop/${p.id}`}
                  className={`brutal-card overflow-hidden group ${isSC ? "hard-shadow-sc" : "hard-shadow-dtf"}`}
                  data-testid={`product-card-${p.id}`}
                >
                  <div className="aspect-[4/3] overflow-hidden border-b border-black bg-[color:var(--muted)]">
                    <img
                      src={p.image}
                      alt={p.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center justify-between gap-2">
                      <span className={isSC ? "brand-tag-sc" : "brand-tag-dtf"}>
                        {p.brand}
                      </span>
                      <span className="label-mono text-[color:var(--muted-text)]">
                        {catLabel}
                      </span>
                    </div>
                    <h3 className={`font-display text-2xl mt-3 leading-tight ${isSC ? "text-sc-purple" : "text-dtf-blue"}`}>
                      {p.name}
                    </h3>
                    <div className="flex items-end justify-between mt-4">
                      <span className="font-mono text-lg font-bold">${p.price.toFixed(2)}</span>
                      <span className={`label-mono inline-flex items-center gap-1 ${isSC ? "group-hover:text-sc-pink" : "group-hover:text-dtf-blue"}`}>
                        View <ArrowUpRight size={14} />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="brutal-card p-10 text-center" data-testid="products-empty">
            <p className="font-display text-3xl">Nothing here yet.</p>
            <p className="font-body text-[color:var(--muted-text)] mt-2">
              Try another category — or request a custom quote.
            </p>
          </div>
        )}
      </section>

      <InstagramFeed />
    </div>
  );
}
